export interface CookieOptions {
  expires?: Date;
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

type CookieDocument = { cookie: string };

const getDocument = (): CookieDocument | null => {
  if (typeof globalThis === 'undefined') return null;
  const candidate = (globalThis as { document?: CookieDocument }).document;
  return candidate ?? null;
};

export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const doc = getDocument();
  if (!doc) return;

  const opts: CookieOptions = {
    path: '/',
    sameSite: 'lax',
    ...options,
  };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (opts.expires) {
    cookieString += `; expires=${opts.expires.toUTCString()}`;
  }

  if (opts.maxAge) {
    cookieString += `; max-age=${opts.maxAge}`;
  }

  if (opts.domain) {
    cookieString += `; domain=${opts.domain}`;
  }

  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }

  if (opts.secure) {
    cookieString += '; secure';
  }

  if (opts.httpOnly) {
    cookieString += '; httponly';
  }

  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }

  doc.cookie = cookieString;
}

export function readCookieFromHeader(cookieHeader: string | undefined | null, name: string): string | null {
  if (!cookieHeader) return null;
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = cookieHeader.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      const raw = cookie.substring(nameEQ.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }

  return null;
}

export function getCookie(name: string): string | null {
  const doc = getDocument();
  if (!doc) return null;
  return readCookieFromHeader(doc.cookie, name);
}

export function deleteCookie(name: string, options: Omit<CookieOptions, 'expires' | 'maxAge'> = {}): void {
  setCookie(name, '', {
    ...options,
    expires: new Date(0),
  });
}

export function hasValidToken(): boolean {
  const token = getCookie('gravity_token');
  
  if (!token || token.trim() === '') {
    return false;
  }

  try {
    if (typeof atob !== 'function') {
      return false;
    }
    // Basic JWT validation - check if it has 3 parts and is not expired
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Decode payload to check expiration
    const payload = JSON.parse(atob(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    
    return payload.exp && payload.exp > now;
  } catch {
    deleteCookie('gravity_token');
    return false;
  }
}
